import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { PlatformTourService, PlatformTourState, PlatformTourStep, TourPlacement } from './platform-tour.service';

interface SpotlightRect { top: number; left: number; width: number; height: number; }
interface TooltipPos { top: number; left: number; arrow: 'top' | 'bottom' | 'left' | 'right' | 'none'; arrowOffset: number; }

const TOOLTIP_WIDTH = 360;
const TOOLTIP_WIDE_WIDTH = 520;
const TOOLTIP_CTA_WIDTH = 480;
const TOOLTIP_ESTIMATED_HEIGHT = 220;
const TOOLTIP_WIDE_HEIGHT = 560;
const TOOLTIP_CTA_HEIGHT = 440;
const GAP = 14;
const VIEWPORT_PADDING = 16;

@Component({
  selector: 'app-platform-tour-overlay',
  templateUrl: './platform-tour-overlay.component.html',
  styleUrls: ['./platform-tour-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlatformTourOverlayComponent implements OnInit, OnDestroy {
  state: PlatformTourState | null = null;
  spotlight: SpotlightRect | null = null;
  tooltip: TooltipPos = { top: 0, left: 0, arrow: 'none', arrowOffset: 50 };
  centered = false;
  visible = false;
  copiedIndex: number | null = null;

  private sub: Subscription | null = null;
  private resizeRafId: number | null = null;
  private trackInterval: any = null;
  private copiedTimeout: any = null;
  private trackedTarget: HTMLElement | null = null;
  private targetClickHandler = (_e: Event) => {
    this.tour.pause();
  };

  constructor(
    private tour: PlatformTourService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  ngOnInit() {
    this.sub = this.tour.state.subscribe(s => {
      const prevIndex = this.state?.currentIndex;
      this.state = s;
      this.visible = !!s?.active;
      if (s && s.currentIndex !== prevIndex) this.resetCopied();
      if (s?.active) {
        this.scheduleRecalc();
        this.startTracking();
      } else {
        this.stopTracking();
      }
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    this.stopTracking();
    this.detachTargetClick();
  }

  @HostListener('window:resize')
  @HostListener('window:scroll', ['$event'])
  onWindowChange() { this.scheduleRecalc(); }

  get step(): PlatformTourStep | null {
    if (!this.state) return null;
    return this.state.steps[this.state.currentIndex] || null;
  }

  get isFirst(): boolean { return !!this.state && this.state.currentIndex === 0; }
  get isLast(): boolean { return !!this.state && this.state.currentIndex === this.state.steps.length - 1; }
  get progress(): number {
    if (!this.state) return 0;
    return ((this.state.currentIndex + 1) / this.state.steps.length) * 100;
  }
  get stepLabel(): string {
    if (!this.state) return '';
    return `${this.state.currentIndex + 1} / ${this.state.steps.length}`;
  }
  get title(): string {
    const s = this.step; if (!s) return '';
    return this.state?.language === 'EN' ? s.titleEn : s.titleDe;
  }
  get body(): string {
    const s = this.step; if (!s) return '';
    return this.state?.language === 'EN' ? s.bodyEn : s.bodyDe;
  }
  get ctaLabel(): string | null {
    const s = this.step; if (!s) return null;
    const label = this.state?.language === 'EN' ? s.ctaLabelEn : s.ctaLabelDe;
    return label || null;
  }
  get ctaUrlValue(): string | null {
    const s = this.step; if (!s) return null;
    return s.ctaUrl || null;
  }
  get ctaUrlLabel(): string | null {
    const s = this.step; if (!s) return null;
    const label = this.state?.language === 'EN' ? s.ctaUrlLabelEn : s.ctaUrlLabelDe;
    return label || null;
  }
  get ctaActionValue(): string | null {
    const s = this.step; if (!s) return null;
    return s.ctaAction || null;
  }
  get ctaActionLabel(): string | null {
    const s = this.step; if (!s) return null;
    const label = this.state?.language === 'EN' ? s.ctaActionLabelEn : s.ctaActionLabelDe;
    return label || null;
  }
  get skipLabel(): string { return this.state?.language === 'EN' ? 'Skip tour' : 'Tour beenden'; }
  get nextLabel(): string { return this.isLast ? (this.state?.language === 'EN' ? 'Finish' : 'Fertig') : (this.state?.language === 'EN' ? 'Next' : 'Weiter'); }
  get prevLabel(): string { return this.state?.language === 'EN' ? 'Back' : 'Zurück'; }
  get copyLabel(): string { return this.state?.language === 'EN' ? 'Click to copy' : 'Klicken zum Kopieren'; }
  get copiedLabel(): string { return this.state?.language === 'EN' ? 'Copied!' : 'Kopiert!'; }
  get isPromptsStep(): boolean { return this.step?.kind === 'prompts'; }
  get isCtaStep(): boolean { return this.step?.kind === 'cta'; }
  get heroIcon(): string | null { return this.step?.heroIcon || null; }

  onSkip() { this.tour.skip(); }
  onNext() { this.tour.next(); }
  onPrev() { this.tour.prev(); }
  async onCta() {
    const s = this.step;
    if (s?.ctaRoute) await this.tour.runCtaRoute(s.ctaRoute);
    this.tour.finish();
  }
  onCtaUrl() {
    const s = this.step;
    if (s?.ctaUrl) {
      try { window.open(s.ctaUrl, '_blank', 'noopener'); } catch {}
    }
  }
  onCtaAction() {
    const s = this.step;
    if (s?.ctaAction) this.tour.emitCtaAction(s.ctaAction);
    this.tour.finish();
  }

  async copyPrompt(text: string, index: number): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
    this.copiedIndex = index;
    this.cdr.markForCheck();
    if (this.copiedTimeout) clearTimeout(this.copiedTimeout);
    this.copiedTimeout = setTimeout(() => {
      this.copiedIndex = null;
      this.cdr.markForCheck();
    }, 1500);
  }

  private resetCopied() {
    this.copiedIndex = null;
    if (this.copiedTimeout) { clearTimeout(this.copiedTimeout); this.copiedTimeout = null; }
  }

  private startTracking() {
    this.stopTracking();
    this.trackInterval = setInterval(() => this.recalc(), 250);
  }
  private stopTracking() {
    if (this.trackInterval) { clearInterval(this.trackInterval); this.trackInterval = null; }
    this.detachTargetClick();
  }

  private attachTargetClick(el: HTMLElement) {
    if (this.trackedTarget === el) return;
    this.detachTargetClick();
    this.trackedTarget = el;
    el.addEventListener('click', this.targetClickHandler, true);
  }

  private detachTargetClick() {
    if (this.trackedTarget) {
      this.trackedTarget.removeEventListener('click', this.targetClickHandler, true);
      this.trackedTarget = null;
    }
  }

  private scheduleRecalc() {
    if (this.resizeRafId !== null) cancelAnimationFrame(this.resizeRafId);
    this.resizeRafId = requestAnimationFrame(() => { this.resizeRafId = null; this.recalc(); });
  }

  private recalc() {
    const s = this.step;
    if (!s) { this.spotlight = null; this.cdr.markForCheck(); return; }

    const wide = s.kind === 'prompts';
    const cta = s.kind === 'cta';
    const w = wide ? TOOLTIP_WIDE_WIDTH : cta ? TOOLTIP_CTA_WIDTH : TOOLTIP_WIDTH;
    const h = wide ? TOOLTIP_WIDE_HEIGHT : cta ? TOOLTIP_CTA_HEIGHT : TOOLTIP_ESTIMATED_HEIGHT;

    if (!s.selector || s.placement === 'center') {
      this.spotlight = null;
      this.centered = true;
      this.detachTargetClick();
      this.tooltip = {
        top: Math.max(VIEWPORT_PADDING, window.innerHeight / 2 - h / 2),
        left: Math.max(VIEWPORT_PADDING, window.innerWidth / 2 - w / 2),
        arrow: 'none',
        arrowOffset: 50
      };
      this.cdr.markForCheck();
      return;
    }

    const el = this.findElement(s.selector);
    if (!el) {
      this.spotlight = null;
      this.centered = true;
      this.detachTargetClick();
      this.tooltip = {
        top: Math.max(VIEWPORT_PADDING, window.innerHeight / 2 - h / 2),
        left: Math.max(VIEWPORT_PADDING, window.innerWidth / 2 - w / 2),
        arrow: 'none',
        arrowOffset: 50
      };
      this.cdr.markForCheck();
      return;
    }

    this.attachTargetClick(el);

    const rect = el.getBoundingClientRect();
    const pad = s.spotlightPadding ?? 6;
    this.spotlight = {
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2
    };
    this.centered = false;
    this.tooltip = this.computeTooltipPosition(this.spotlight, s.placement || 'auto', w, h);
    this.cdr.markForCheck();
  }

  private findElement(selector: string | string[]): HTMLElement | null {
    const selectors = Array.isArray(selector) ? selector : [selector];
    for (const sel of selectors) {
      const el = document.querySelector(sel) as HTMLElement | null;
      if (el && el.getClientRects().length > 0) return el;
    }
    return null;
  }

  private computeTooltipPosition(spot: SpotlightRect, placement: TourPlacement, width: number, height: number): TooltipPos {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const tryPlace = (p: Exclude<TourPlacement, 'auto' | 'center'>): TooltipPos => {
      let top = 0, left = 0;
      let arrow: TooltipPos['arrow'] = 'none';
      let arrowOffset = 50;
      switch (p) {
        case 'right':
          top = spot.top + spot.height / 2 - height / 2;
          left = spot.left + spot.width + GAP;
          arrow = 'left';
          break;
        case 'left':
          top = spot.top + spot.height / 2 - height / 2;
          left = spot.left - width - GAP;
          arrow = 'right';
          break;
        case 'top':
          top = spot.top - height - GAP;
          left = spot.left + spot.width / 2 - width / 2;
          arrow = 'bottom';
          break;
        case 'bottom':
        default:
          top = spot.top + spot.height + GAP;
          left = spot.left + spot.width / 2 - width / 2;
          arrow = 'top';
          break;
      }
      const clampedLeft = Math.max(VIEWPORT_PADDING, Math.min(left, vw - width - VIEWPORT_PADDING));
      const clampedTop = Math.max(VIEWPORT_PADDING, Math.min(top, vh - height - VIEWPORT_PADDING));
      if (arrow === 'top' || arrow === 'bottom') {
        const anchorX = spot.left + spot.width / 2;
        arrowOffset = Math.max(20, Math.min(width - 20, anchorX - clampedLeft));
      } else if (arrow === 'left' || arrow === 'right') {
        const anchorY = spot.top + spot.height / 2;
        arrowOffset = Math.max(20, Math.min(height - 20, anchorY - clampedTop));
      }
      return { top: clampedTop, left: clampedLeft, arrow, arrowOffset };
    };

    if (placement !== 'auto') {
      return tryPlace(placement as Exclude<TourPlacement, 'auto' | 'center'>);
    }

    const spaceRight = vw - (spot.left + spot.width);
    const spaceLeft = spot.left;
    const spaceBottom = vh - (spot.top + spot.height);
    const spaceTop = spot.top;
    const max = Math.max(spaceRight, spaceLeft, spaceBottom, spaceTop);
    if (max === spaceRight && spaceRight > width + GAP + VIEWPORT_PADDING) return tryPlace('right');
    if (max === spaceLeft && spaceLeft > width + GAP + VIEWPORT_PADDING) return tryPlace('left');
    if (max === spaceBottom) return tryPlace('bottom');
    return tryPlace('top');
  }
}
